import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import { 
    CheckCircle, 
    Save, 
    ExpandMore,
    History
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { userRequest } from '../../requestMethods';

const WritingPractice = ({ 
    contentType, 
    contentId, 
    onAICheck 
}) => {
    const [writingAnswer, setWritingAnswer] = useState('');
    const [writingNotes, setWritingNotes] = useState('');
    const [previousAnswers, setPreviousAnswers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [practiceId, setPracticeId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Fetch existing practice data on mount
    const fetchPracticeData = useCallback(async () => {
        if (!contentType || !contentId) return;

        setLoading(true);
        try {
            const response = await userRequest.get(`/practice-writing/${contentType}/${contentId}`);
            if (response.data.exists) {
                setPracticeId(response.data._id);
                setWritingNotes(response.data.userNotes || '');
                setPreviousAnswers(response.data.userAnswers || []);
                // Set the most recent answer as current
                if (response.data.userAnswers && response.data.userAnswers.length > 0) {
                    setWritingAnswer(response.data.userAnswers[0]);
                }
            }
        } catch (error) {
            // 404 means no practice data yet, which is fine
            if (error.response?.status !== 404) {
                console.error('Yozish mashqi ma\'lumotlarini olishda xatolik:', error);
            }
        } finally {
            setLoading(false);
        }
    }, [contentType, contentId]);

    useEffect(() => {
        fetchPracticeData();
    }, [fetchPracticeData]);

    const handleSave = async () => {
        if (!contentType || !contentId) {
            setSnackbar({
                open: true,
                message: 'Kontent ma\'lumotlari mavjud emas',
                severity: 'error'
            });
            return;
        }

        setSaving(true);
        try {
            const response = await userRequest.post('/practice-writing', {
                contentType,
                contentId,
                userNotes: writingNotes,
                userAnswer: writingAnswer
            });

            setPracticeId(response.data._id);
            setPreviousAnswers(response.data.userAnswers || []);
            
            setSnackbar({
                open: true,
                message: 'Yozish mashqi muvaffaqiyatli saqlandi!',
                severity: 'success'
            });
        } catch (error) {
            console.error('Yozish mashqini saqlashda xatolik:', error);
            setSnackbar({
                open: true,
                message: 'Saqlashda xatolik yuz berdi',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleAICheck = () => {
        if (onAICheck) {
            onAICheck('Writing', { answer: writingAnswer, notes: writingNotes });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const loadPreviousAnswer = (answer) => {
        setWritingAnswer(answer);
    };

    if (loading) {
        return (
            <Paper 
                elevation={2} 
                sx={{ 
                    p: 3, 
                    mb: 3,
                    border: '2px solid',
                    borderColor: 'secondary.main',
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px'
                }}
            >
                <CircularProgress />
            </Paper>
        );
    }

    return (
        <Paper 
            elevation={2} 
            sx={{ 
                p: 3, 
                mb: 3,
                border: '2px solid',
                borderColor: 'secondary.main',
                borderRadius: 2,
                backgroundColor: 'background.paper'
            }}
        >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: 'secondary.main' }}>
                ✍️ Yozish Mashqi
            </Typography>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                💡 Yozish va eslatmalaringizni tartibga solish uchun maslahatlar:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 2 }}>
                • Asosiy savolga javob beruvchi aniq kirish bilan boshlang<br/>
                • Fikrlaringizni tartibga solish uchun nuqtali yoki raqamli ro'yxatlardan foydalaning<br/>
                • Fikrlaringizni qo'llab-quvvatlash uchun misollar yoki dalillar keltiring<br/>
                • Ishlatmoqchi bo'lgan asosiy so'zlarni yozing<br/>
                • Mashq qilmoqchi bo'lgan grammatika tuzilmalari yoki iboralarni qayd qiling<br/>
                • Asosiy fikrlaringizni umumlashtiruvchi qisqacha xulosa bilan yakunlang
            </Typography>

            <TextField
                fullWidth
                label="Bu yerda eslatmalar yozing"
                multiline
                rows={6}
                value={writingNotes}
                onChange={(e) => setWritingNotes(e.target.value)}
                placeholder="G'oyalar, lug'at yoki fikrlaringizni yozing..."
                sx={{ mb: 2 }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Vazifaga javobingizni yozing. Ushbu sahifadagi Vazifa, Javob va Yangi So'zlarga murojaat qilishingiz mumkin.
            </Typography>
            <TextField
                fullWidth
                label="Sizning javobingiz"
                multiline
                rows={8}
                value={writingAnswer}
                onChange={(e) => setWritingAnswer(e.target.value)}
                placeholder="Javobingizni bu yerga yozing..."
                sx={{ mb: 3 }}
            />

            {/* Previous Answers Section */}
            {previousAnswers.length > 1 && (
                <Accordion sx={{ mb: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <History />
                            <Typography>Oldingi javoblar</Typography>
                            <Chip 
                                label={previousAnswers.length} 
                                size="small" 
                                color="secondary" 
                            />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {previousAnswers.slice(1).map((answer, index) => (
                            <Box 
                                key={index}
                                sx={{ 
                                    p: 2, 
                                    mb: 1, 
                                    bgcolor: 'grey.100', 
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'grey.200' }
                                }}
                                onClick={() => loadPreviousAnswer(answer)}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    Versiya {previousAnswers.length - index - 1} - yuklab olish uchun bosing
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        mt: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical'
                                    }}
                                >
                                    {answer}
                                </Typography>
                            </Box>
                        ))}
                    </AccordionDetails>
                </Accordion>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    onClick={handleSave}
                    disabled={saving || (!writingAnswer.trim() && !writingNotes.trim())}
                >
                    {saving ? 'Saqlanmoqda...' : 'Saqlash'}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircle />}
                    onClick={handleAICheck}
                    disabled={!writingAnswer.trim()}
                >
                    AI Tekshiruvi
                </Button>
            </Box>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={4000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

WritingPractice.propTypes = {
    contentType: PropTypes.oneOf([
        'ContentShortStory', 
        'ContentLongPost', 
        'ContentVideoPost', 
        'AlgorithmicProblem'
    ]),
    contentId: PropTypes.string,
    onAICheck: PropTypes.func
};

export default WritingPractice;
